import { useState, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { SelectReasonMaintenance } from '../../components/FrotaFormularioComponents/SelectReasonMaintenance';
import { SelectWorkshop } from '../../components/FrotaFormularioComponents/SelectWorkshop';

export function MaintenanceDialog({ vehicle, open, onOpenChange, onSuccess }) {
  const [idReasonSelected, setIdReasonSelected] = useState(null);
  const [idWorkshop, setWorkshop] = useState(null);

  const [submitting, setSubmitting] = useState(false);
  const [maintenanceForm, setMaintenanceForm] = useState({
    placa: vehicle?.placa || '',
    prazo: format(new Date().setDate(new Date().getDate() + 7), 'yyyy-MM-dd'),
    usuario: 'admin',
    motivo_id: null,
    oficina_id: null,
  });

  useEffect(() => {
    if (vehicle) {
      setMaintenanceForm((prev) => ({
        ...prev,
        placa: vehicle.placa,
      }));
    }
  }, [vehicle]);

  useEffect(() => {
    if (idReasonSelected) {
      setMaintenanceForm((prev) => ({
        ...prev,
        motivo_id: idReasonSelected,
      }));
    }
  }, [idReasonSelected]);

  useEffect(() => {
    if (idWorkshop) {
      setMaintenanceForm((prev) => ({
        ...prev,
        oficina_id: idWorkshop,
      }));
    }
  }, [idWorkshop]);

  const handleMaintenanceFormChange = (e) => {
    const { name, value, type } = e.target;
    setMaintenanceForm({
      ...maintenanceForm,
      [name]: type === 'number' ? Number(value) : value,
    });
  };

  const handleMaintenanceFormSubmit = async (e) => {
    e.preventDefault();
    if (!vehicle) return;
    setSubmitting(true);
    let processedData = preprocessFormData(maintenanceForm);

    try {
      const response = await axios.post(
        'https://radar.tlpservicos.com.br/api/manutencoes',
        processedData,
        {
          headers: {
            'Content-Type': 'application/json',
            accept: 'application/json',
          },
        }
      );

      if (response.status === 200 || response.status === 201) {
        toast.success(`${vehicle.placa} enviado para manutenção com sucesso!`);
        onOpenChange(false);
        if (onSuccess) onSuccess();

        // Log dos dados da resposta (opcional)
      } else {
        toast.error('Erro ao registrar manutenção');
      }
    } catch (error) {
      console.error('Erro ao enviar dados:', error);
      toast.error('Erro ao registrar manutenção. Verifique os dados e tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  // Função para pré-processar os dados do formulário antes de enviar
  const preprocessFormData = (data) => {
    const processedData = { ...data };

    const currentDate = new Date().toISOString().split('T')[0];
    if (processedData.prazo < currentDate) {
      processedData.prazo = currentDate;
    }
    return processedData;
  };

  const validateForm = () => {
    if (maintenanceForm.valor <= 0) {
      toast.error('O valor da manutenção deve ser maior que zero.');
      return false;
    }

    const today = new Date();
    const prazoDate = new Date(maintenanceForm.prazo);
    if (prazoDate < today) {
      toast.error('O prazo não pode ser uma data no passado.');
      return false;
    }

    return true;
  };

  const handleSubmitWithValidation = (e) => {
    e.preventDefault();

    if (validateForm()) {
      handleMaintenanceFormSubmit(e);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="bg-black/50 backdrop-blur-sm data-[state=open]:animate-overlayShow fixed inset-0" />
        <Dialog.Content className="data-[state=open]:animate-contentShow fixed top-[50%] left-[50%] max-h-[85vh] w-[90vw] max-w-md translate-x-[-50%] translate-y-[-50%] rounded-md bg-gray-300 p-6 shadow-lg focus:outline-none">
          <Dialog.Title className="text-xl font-semibold text-gray-100">
            Registrar Manutenção
          </Dialog.Title>

          <Dialog.Description className="mt-2 mb-5 text-sm text-gray-100">
            Preencha os detalhes para enviar o veículo para manutenção
          </Dialog.Description>

          <form onSubmit={handleSubmitWithValidation} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-100 mb-1">
                Veículo
              </label>
              <input
                type="text"
                value={vehicle ? `${vehicle.placa} - ${vehicle.descricao}` : ''}
                className="w-full border border-gray-300 rounded-md px-3 py-2  text-gray-900"
              />
            </div>

            <div>
              <label
                htmlFor="prazo"
                className="block text-sm font-medium text-gray-100 mb-1"
              >
                Motivo envio para Manutenção*
              </label>
              <SelectReasonMaintenance
                onValueChange={setIdReasonSelected}
                placeholder="Selecione o motivo"
              />
            </div>

            <div>
              <label
                htmlFor="prazo"
                className="block text-sm font-medium text-gray-100 mb-1"
              >
                Seleção de Oficina*
              </label>
              <SelectWorkshop
                onValueChange={setWorkshop}
                placeholder="Selecione o oficina"
              />
            </div>

            <div className="pt-4 border-t border-gray-400 flex justify-end gap-2">
              <Dialog.Close asChild>
                <button
                  type="button"
                  className="px-4 py-2 bg-gray-700 text-gray-100 rounded-md hover:bg-gray-600 transition-colors"
                >
                  Cancelar
                </button>
              </Dialog.Close>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Enviando...
                  </>
                ) : (
                  'Registrar Manutenção'
                )}
              </button>
            </div>
          </form>

          <Dialog.Close asChild>
            <button
              className="text-gray-700 hover:text-gray-900 absolute top-4 right-4 focus:outline-none"
              aria-label="Fechar"
            >
              <X size={24} />
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/*
            <div>
              <label
                htmlFor="prazo"
                className="block text-sm font-medium text-gray-100 mb-1"
              >
                Prazo de Conclusão Estimado*
              </label>
              <input
                type="date"
                id="prazo"
                name="prazo"
                value={maintenanceForm.prazo}
                onChange={handleMaintenanceFormChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900"
                min={format(new Date(), 'yyyy-MM-dd')}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-100 mb-1">
                Oficina
              </label>
              <SelectWorkshop />
            </div>

            <div>
              <label
                htmlFor="prazo"
                className="block text-sm font-medium text-gray-100 mb-1"
              >
                Motivo envio para Manutenção*
              </label>
              <SelectReasonMaintenance
                onValueChange={setIdReasonSelected}
                placeholder="Selecione o motivo"
              />
            </div>

*/
